import React, {FunctionComponent, useEffect, useState} from 'react'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {addMonths, differenceInMonths, endOfMonth, isSameDay, parseISO, startOfMonth} from "date-fns";
import {useDays} from "@App/hooks/useDays";
import useStore from "@App/store";
import {Loader} from "@App/components/common/Loader";
import {Slot} from "@App/types";

type CalendarProps = {
    onChange: (event: Date) => void
    onNoAvailableDays?: (available: boolean) => void
    daysLoading?: (loading: boolean) => void
    nextSlot?: Slot|null
    className?: string
    selectedDay?: Date|null
}

export const DayPicker: FunctionComponent<CalendarProps> = (props: CalendarProps) => {

    const state = useStore()

    const garage = state.order.garage
    const slot = state.order.slot
    const [earliestStartDate, setEarliestStartDate] = useState(garage && garage.available_days.length > 0
        ? startOfMonth(parseISO(garage.available_days[0].available_at.toString()))
        : startOfMonth(new Date())
    )


    const [startDate] = useState(slot ? parseISO(slot.starts_at) : earliestStartDate)
    const [from, setFrom] = useState(startOfMonth(startDate))
    const [to, setTo] = useState(endOfMonth(startDate))
    const [activeStartDate, setActiveStartDate] = useState(new Date())
    const [monthChanged, setMonthChanged] = useState(false)
    const [userInputReceived, setUserInputReceived] = useState(false)

    const {loading, days, error} = useDays(from, to)

    useEffect(() => {
        if (props.nextSlot) {
            setEarliestStartDate(startOfMonth(parseISO(props.nextSlot.starts_at)))
        }
    }, [props.nextSlot])

    useEffect(() => {
        setActiveStartDate(startOfMonth(startDate))
    }, []);


    useEffect(() => {
        // select the first available day on initial load
        // if (!loading && !props.selectedDay && days) {
        //     props.onChange && props.onChange(days[0])
        // }
        // return if there are available days in the selected month
        if (days && !loading && props.onNoAvailableDays) {
            props.onNoAvailableDays(days.length > 0)
        }
        if (monthChanged && !loading && props.selectedDay && props.selectedDay.getMonth() !== activeStartDate.getMonth()) {
            props.onChange && props.onChange(days[0])
            setMonthChanged(false)
        }
        if (!loading && !userInputReceived && days.length === 0 && differenceInMonths(activeStartDate, new Date()) < 1) {
            onChangeMonth(
                {
                    "activeStartDate": addMonths(activeStartDate, 1),
                })
        }
        props.daysLoading && props.daysLoading(loading)
    }, [loading, days, error])

    // if the picked date changed from an external source
    // reset the calendar to the right month
    useEffect(() => {
        if (props.selectedDay && days && days.length > 0 && props.selectedDay !== days[0]) {
            onChangeMonth(
                {
                    "activeStartDate": props.selectedDay,
                })
            setMonthChanged(false)
        }
    }, [props.selectedDay])

    const changeDay = async (selectedDay: Date) => {
        props.onChange(selectedDay)
    }

    const checkDay = (day: Date) => {
        return !days.some((date) => isSameDay(date, day))
    }

    const onChangeMonth = (value: any) => {
        setActiveStartDate(value.activeStartDate)
        setFrom(startOfMonth(value.activeStartDate))
        setTo(endOfMonth(value.activeStartDate))
        setMonthChanged(true)
        setUserInputReceived(true)
    }

    return (
        <div className={'relative'}>
            <Calendar
                onChange={changeDay}
                minDate={startOfMonth(earliestStartDate)}
                maxDate={addMonths(endOfMonth(new Date()), 3)}
                value={props.selectedDay && new Date(props.selectedDay)}
                className={`
                mt-3
                border
                border-1
                border-gray-100
                font-serif
                p-3
                w-full
                md:w-96 h-full
                ${loading ? 'animate-pulse opacity-90 blur-sm' : ''}
                ${props.className}`}
                tileDisabled={({date}: any) => checkDay(date)}
                minDetail="month"
                locale={'de-DE'}
                onActiveStartDateChange={onChangeMonth}
                activeStartDate={activeStartDate}
            />
            {loading && (
                <div className={'absolute top-0 pt-8 text-2xl font-serif w-full text-center border border-1 border-gray-300 rounded-lg h-80'}>
                    <div className={'pb-6'}>Werkstattkalender <br/> wird geladen...</div>
                    <Loader
                        loaderSizeClass={'w-24 h-24'}
                        logoSizeClass={'w-12 h-12'}/>
                </div>
            )}
        </div>
    )
}
